
import React, { useState, useEffect, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import DocumentHeader from "./DocumentHeader";
import DocumentEditor from "./DocumentEditor";
import DocumentLoadingScreen from "./DocumentLoadingScreen";
import PrintableDocument from "./PrintableDocument";
import { useReactToPrint } from 'react-to-print';
import { generatePdfFromElement } from "@/utils/pdfGenerator";

interface DocumentPreviewProps {
  document: any;
  content: string;
  onContentChange: (content: string) => void;
  headerContent?: string;
  footerContent?: string;
  onHeaderChange?: (header: string) => void;
  onFooterChange?: (footer: string) => void;
  onBack: () => void;
  onSave: () => void;
  isBlankDocument?: boolean;
  isAIWriting?: boolean;
  onAIWritingChange?: (writing: boolean) => void;
  onStreamToDocument?: (payload: { mode: string; text: string }) => void;
  onTextSelection?: (text: string, position: { start: number; end: number }) => void;
  editPosition?: any;
  isCreatingDocument?: boolean;
  onDocumentCreated?: () => void;
  aiContent?: string;
  editorVersion?: number;
}

interface PdfSettings {
  company_name?: string;
  header_text?: string;
  footer_text?: string;
  header_image_url?: string;
  font_family?: string;
  font_size_body?: number;
  font_size_footnote?: number;
  margin_top?: number;
  margin_bottom?: number;
  margin_left?: number;
  margin_right?: number;
  line_spacing?: number;
  paragraph_indent?: number;
  page_numbering_enabled?: boolean;
  page_numbering_position?: string;
  lawyer_name?: string;
  oab_number?: string;
  oab_state?: string;
  include_lawyer_signature?: boolean;
}

const DocumentPreview = ({ 
  document, 
  content, 
  onContentChange,
  headerContent,
  footerContent,
  onHeaderChange,
  onFooterChange,
  onBack, 
  onSave, 
  isBlankDocument = false,
  isAIWriting = false,
  onAIWritingChange,
  onStreamToDocument,
  onTextSelection,
  editPosition,
  isCreatingDocument = false,
  onDocumentCreated,
  aiContent = '',
  editorVersion = -1
}: DocumentPreviewProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [pdfSettings, setPdfSettings] = useState<PdfSettings | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      loadPdfSettings();
    }
  }, [user]);

  const loadPdfSettings = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('pdf_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao carregar configurações de PDF:', error);
        return;
      }

      setPdfSettings(data);
    } catch (error) {
      console.error('Erro ao carregar configurações de PDF:', error);
    }
  };

  const handleSave = () => {
    onSave();
    toast({
      title: "Documento salvo",
      description: "Suas alterações foram salvas com sucesso.",
    });
  };

  const sanitizeFilename = (filename: string): string => {
    if (!filename) {
      return `documento_${Date.now()}`;
    }

    // Mapeamento de caracteres acentuados para ASCII
    const accentMap: { [key: string]: string } = {
      'à': 'a', 'á': 'a', 'â': 'a', 'ã': 'a', 'ä': 'a', 'å': 'a',
      'è': 'e', 'é': 'e', 'ê': 'e', 'ë': 'e',
      'ì': 'i', 'í': 'i', 'î': 'i', 'ï': 'i',
      'ò': 'o', 'ó': 'o', 'ô': 'o', 'õ': 'o', 'ö': 'o',
      'ù': 'u', 'ú': 'u', 'û': 'u', 'ü': 'u',
      'ý': 'y', 'ÿ': 'y',
      'ñ': 'n', 'ç': 'c',
      'À': 'A', 'Á': 'A', 'Â': 'A', 'Ã': 'A', 'Ä': 'A', 'Å': 'A',
      'È': 'E', 'É': 'E', 'Ê': 'E', 'Ë': 'E',
      'Ì': 'I', 'Í': 'I', 'Î': 'I', 'Ï': 'I',
      'Ò': 'O', 'Ó': 'O', 'Ô': 'O', 'Õ': 'O', 'Ö': 'O',
      'Ù': 'U', 'Ú': 'U', 'Û': 'U', 'Ü': 'U',
      'Ý': 'Y', 'Ÿ': 'Y',
      'Ñ': 'N', 'Ç': 'C'
    };

    // Substituir caracteres acentuados
    let sanitized = filename;
    for (const [accented, ascii] of Object.entries(accentMap)) {
      sanitized = sanitized.replace(new RegExp(accented, 'g'), ascii);
    }

    // Substituir espaços por underscores e remover caracteres especiais
    sanitized = sanitized.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');

    // Garantir que não fica vazio após sanitização
    if (!sanitized || sanitized.length === 0) {
      sanitized = `documento_${Date.now()}`;
    }

    // Limitar tamanho do nome (máximo 100 caracteres)
    if (sanitized.length > 100) {
      sanitized = sanitized.substring(0, 100);
    }

    return sanitized;
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current) {
      toast({
        title: "Erro",
        description: "Elemento não encontrado para gerar PDF.",
        variant: "destructive",
      });
      return;
    }

    setIsDownloading(true);
    console.log('DocumentPreview - Iniciando geração de PDF para documento:', document.title);

    try {
      // Aguardar 1 frame para garantir layout completo
      await new Promise<void>(resolve => 
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
      );

      const settings = pdfSettings || {};
      const sanitizedTitle = sanitizeFilename(document.title);
      const filename = `${sanitizedTitle}_${Date.now()}.pdf`;

      // Usar novo gerador de PDF com html2canvas + jsPDF
      await generatePdfFromElement(printRef.current, {
        filename,
        margin: settings.margin_left || 30,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: window.devicePixelRatio || 2,
          useCORS: true,
          allowTaint: true,
          logging: false
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait',
          compress: true
        }
      });

      console.log('DocumentPreview - PDF gerado e baixado com sucesso');
      toast({
        title: "PDF gerado com sucesso",
        description: "O download do seu documento foi iniciado.",
      });
    } catch (error) {
      console.error('DocumentPreview - Erro ao gerar PDF:', error);
      toast({
        title: "Erro ao gerar PDF",
        description: "Erro técnico ao gerar o PDF. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    onBeforePrint: async () => {
      console.log('DocumentPreview - Iniciando impressão para documento:', document.title);
    },
    pageStyle: `
      @page {
        size: A4;
        margin: 0;
      }
      
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          color-adjust: exact;
        }
      }
    `
  });

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    toast({
      title: "Copiado!",
      description: "Conteúdo copiado para a área de transferência.",
    });
  };

  // 📊 Log de debug
  console.log('[PREVIEW] loading:', isCreatingDocument, 'aiLen:', aiContent?.length || 0, 'v:', editorVersion);

  return (
    <div 
      className="h-full flex flex-col rounded-lg min-h-0"
    >
      {/* Barra de ações movida para dentro do DocumentEditor */}
      
      {/* ✅ SEMPRE renderiza o editor + overlay quando carregando */}
      <div className="relative flex-1">
        <DocumentEditor
          document={document}
          content={content}
          onContentChange={onContentChange}
          headerContent={headerContent}
          footerContent={footerContent}
          onHeaderChange={onHeaderChange}
          onFooterChange={onFooterChange}
          isBlankDocument={isBlankDocument}
          isAIWriting={isAIWriting}
          onAIWritingChange={onAIWritingChange}
          onStreamToDocument={onStreamToDocument}
          onTextSelection={onTextSelection || ((text, position) => {
            console.log('[PREVIEW] Text selected:', text.substring(0, 50) + '...', position);
          })}
          editPosition={editPosition}
          aiContent={aiContent}
          editorVersion={editorVersion}
          onDocumentCreated={onDocumentCreated}
          onBack={onBack}
          onSave={handleSave}
          onDownloadPDF={handleDownloadPDF}
          onPrint={handlePrint}
          onCopy={handleCopy}
          isDownloading={isDownloading}
        />
        
        {/* 🎭 OVERLAY de loading (não bloqueia montagem do editor) */}
        {isCreatingDocument && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
            <DocumentLoadingScreen />
          </div>
        )}
      </div>

      {/* Componente invisível para impressão/PDF - Posicionamento offscreen para html2canvas */}
      <div style={{ 
        position: 'absolute', 
        left: '-9999px', 
        top: '-9999px',
        width: '794px',
        background: 'white'
      }}>
        <PrintableDocument
          ref={printRef}
          document={document}
          content={content}
          pdfSettings={pdfSettings}
        />
      </div>
    </div>
  );
};

export default DocumentPreview;
